import Layout from '@/components/Layout'
import { GetServerSidePropsContext } from 'next'
import { useRouter } from 'next/router'
import { getReferencesCollection } from '@/fetchers'
import { Reference } from '@mpe/api-client/models/entities/reference'
import Pagination from '@/components/Pagination/Pagination'
import { Link } from '@/components/Link/Link'

const sortItems = (items: any[]): { sources: any[]; references: any[] } => {
  const sourceItems: any[] = []
  const referenceItems: any[] = []

  items.forEach((item: any) => {
    if (item.editorState === 'readonly') {
      sourceItems.push(item)
      return
    }

    referenceItems.push(item)
  })

  return { sources: sourceItems, references: referenceItems }
}

const referenceFields: any = {
  authors: { className: 'small-caps', label: null },
  title: { className: 'italic', label: null },
  subTitle: { className: 'italic', label: null },
  link: { label: 'Url', format: 'url', className: 'max-w-full inline-block truncate' },
  date: { label: 'Év' },

  series: { label: 'Sorozat' },
  translation: { label: 'Fordítás' },
  inclusiveBook: { label: 'Bennfoglaló könyv' },
  editor: { label: 'Szerkesztő' },
  location: { label: 'Hely' },
  publisher: { label: 'Kiadó' },
  isbn: { label: 'ISBN/ISSN' },
  doi: { label: 'DOI azonosító' },
  newspaper: { label: 'Folyóirat neve' },
  year: { label: 'Évfolyam' },
  issue: { label: 'Lapszám' },
  visitDate: { label: 'Megtekintés időpontja' }
}

const ReferenceElm = (props: any) => {
  if (!props.item) {
    return null
  }

  const ref: Reference = new Reference(props.item)

  return (
    <div className="w-full md:w-1/2 xl:w-1/3 pl-4 pr-4 mb-8 entity-reference-item">
      {ref.getFieldsConfig().map((field: any, i) => {
        const fieldName = field.field || field.path || field.toString()
        const fieldValue = field.field
          ? (ref as any)[fieldName]
          : (ref.referenceData as any)[fieldName]

        if (!fieldValue || fieldValue === '') {
          return null
        }

        const className = referenceFields[fieldName]?.className || field.className || null
        const format = referenceFields[fieldName]?.format || null

        let formattedValue
        switch (format) {
          case 'url':
            formattedValue = (
              <span className={className}>
                <Link className="hover:underline" href={fieldValue} target="_blank">
                  {fieldValue}
                </Link>
              </span>
            )
            break

          default:
            formattedValue = <span className={className}>{fieldValue}</span>
        }

        return (
          <div key={i} className="flex">
            {referenceFields[fieldName]?.label ? (
              <>
                <span className="mr-2 text-lightGray">{referenceFields[fieldName].label}:</span>
              </>
            ) : null}
            {formattedValue}
          </div>
        )
      })}
    </div>
  )
}

const ReferencesCollection = (props: any) => {
  const router = useRouter()
  const { sources, references } = sortItems(props.items || [])
  const page = props.page || 1

  const onPrev = () => {
    if (page > 1) {
      onPage(page - 1)
    }
  }

  const onNext = () => {
    if (page < props.pagesCount) {
      onPage(page + 1)
    }
  }

  const onPage = (page: Number) => {
    router.push(`/hivatkozasok-gyujtemenye/${page}`)
  }

  return (
    <>
      <Layout>
        <div className="container-padded pt-20">
          <div>
            <h2 className="mt-3 md:mt-5 text-3xl lg:text-5xl font-bold tracking-wide">
              Hivatkozások gyűjteménye
            </h2>
            {sources && sources.length > 0 && (
              <>
                <h2 className="mt-3 md:mt-5 text-xl lg:text-2xl font-bold tracking-wide">
                  Források
                </h2>
                <div className="my-10">
                  <div className="flex flex-wrap">
                    {sources.map((item: Reference, i: number) => {
                      return <ReferenceElm key={i} item={item} />
                    })}
                  </div>
                </div>
              </>
            )}
            {references && references.length > 0 && (
              <>
                <h2 className="mt-3 md:mt-5 text-xl lg:text-2xl font-bold tracking-wide">
                  Hivatkozások
                </h2>
                <div className="my-10">
                  <div className="flex flex-wrap">
                    {references.map((item: Reference, i: number) => {
                      return <ReferenceElm key={i} item={item} />
                    })}
                  </div>
                </div>
              </>
            )}
            {(!sources || sources.length === 0) && (!references || references.length === 0) && (
              <div className="my-10 py-36 text-center text-lightGray italic">
                <p>Nem találhatók hivatkozások</p>
              </div>
            )}
            {props.pagesCount > 1 && (
              <Pagination
                pagesCount={props.pagesCount}
                currentPage={page}
                onPrev={onPrev}
                onNext={onNext}
                onPage={onPage}
              />
            )}
          </div>
        </div>
      </Layout>
    </>
  )
}

export async function getServerSideProps(context: GetServerSidePropsContext): Promise<any> {
  const page = context.query['page'] ? Number(context.query['page'][0]) : 1
  const response = await getReferencesCollection(page)

  return {
    props: { ...response, page } || { items: null }
  }
}

export default ReferencesCollection
